import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../libs/axios';
import Breadcrumb from '../../utils/breadcrumbs';
import { ListLoader } from '../../utils/loader';

type Tab = 'self' | 'soft';

const Registrations: React.FC = () => {
  const [tab, setTab] = useState<Tab>('self');
  const [rows, setRows] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState<any>(null);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [creatorId, setCreatorId] = useState('');
  const [creatorFilter, setCreatorFilter] = useState('');
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const cf = tab === 'soft' && creatorFilter ? `&creator_id=${creatorFilter}` : '';
      const res: any = await api.get(`/moderation/registrations?filter=${tab}&page=${page}&limit=25${q ? `&search=${encodeURIComponent(q)}` : ''}${cf}`);
      setRows(res.data?.data || []);
      setMeta(res.data?.pagination || null);
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [tab, page, q, creatorFilter]);

  const switchTab = (t: Tab) => { setTab(t); setPage(1); };
  const runSearch = () => { setPage(1); setQ(search.trim()); };

  const badge = (s: string) => {
    const map: Record<string, string> = {
      verified: 'bg-green-100 text-green-700', suspended: 'bg-red-100 text-red-700',
      initiated: 'bg-gray-100 text-gray-600', pending: 'bg-yellow-100 text-yellow-700',
    };
    return map[s] || 'bg-gray-100 text-gray-600';
  };

  const tabBtn = (t: Tab, label: string) => (
    <button onClick={() => switchTab(t)}
      className={`px-4 py-2 text-sm font-medium rounded-lg ${tab === t ? 'bg-primary-600 text-white' : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
      {label}
    </button>
  );

  return (
    <div className="p-4 sm:p-6">
      <Breadcrumb />
      <div className="mt-4 mb-5">
        <h1 className="text-xl font-semibold text-gray-900">Registrations</h1>
        <p className="text-sm text-gray-500">Everyone who signed up, and players a user created who never logged in.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {tabBtn('self', 'New sign-ups')}
        {tabBtn('soft', 'Created — not logged in')}
        <div className="flex flex-wrap gap-2 ml-auto">
          {tab === 'soft' && (
            <div className="flex gap-1">
              <input value={creatorId} onChange={(e) => setCreatorId(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (setPage(1), setCreatorFilter(creatorId.trim()))}
                placeholder="Creator user ID" className="rounded-lg border border-gray-300 px-3 py-2 text-sm w-36" />
              <button onClick={() => { setPage(1); setCreatorFilter(creatorId.trim()); }} className="bg-gray-800 hover:bg-gray-900 text-white text-sm px-3 py-2 rounded-lg">Filter</button>
              {creatorFilter && <button onClick={() => { setCreatorId(''); setCreatorFilter(''); setPage(1); }} className="text-gray-500 text-sm px-2">✕</button>}
            </div>
          )}
          <div className="flex gap-2">
            <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && runSearch()}
              placeholder="Search name / phone" className="rounded-lg border border-gray-300 px-3 py-2 text-sm w-56" />
            <button onClick={runSearch} className="bg-gray-800 hover:bg-gray-900 text-white text-sm px-4 py-2 rounded-lg">Search</button>
          </div>
        </div>
      </div>
      {tab === 'soft' && creatorFilter && (
        <p className="-mt-2 mb-3 text-sm text-gray-500">Showing players created by user <span className="font-medium text-gray-700">#{creatorFilter}</span></p>
      )}

      {loading ? <ListLoader /> : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left font-medium px-4 py-3">Name</th>
                  <th className="text-left font-medium px-4 py-3">User ID</th>
                  <th className="text-left font-medium px-4 py-3">Phone</th>
                  {tab === 'soft' && <th className="text-left font-medium px-4 py-3">Created by</th>}
                  {tab === 'soft' && <th className="text-left font-medium px-4 py-3">Creator ID</th>}
                  <th className="text-left font-medium px-4 py-3">Status</th>
                  <th className="text-left font-medium px-4 py-3">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.length === 0 ? (
                  <tr><td colSpan={tab === 'soft' ? 7 : 5} className="px-4 py-8 text-center text-gray-400">No records</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.user_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-900">{r.name || <span className="text-gray-400">(no name)</span>}</td>
                    <td className="px-4 py-3 text-gray-500">#{r.user_id}</td>
                    <td className="px-4 py-3 text-gray-600">{r.phone_number || '-'}</td>
                    {tab === 'soft' && <td className="px-4 py-3 text-gray-700">{r.creator_name || <span className="text-gray-400">-</span>}</td>}
                    {tab === 'soft' && <td className="px-4 py-3 text-gray-500">
                      {r.created_by
                        ? <button onClick={() => { setCreatorId(String(r.created_by)); setCreatorFilter(String(r.created_by)); setPage(1); }} className="text-primary-600 hover:underline">#{r.created_by}</button>
                        : '-'}
                    </td>}
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs capitalize ${badge(r.status)}`}>{r.status}</span></td>
                    <td className="px-4 py-3 text-gray-500">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {meta && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-600">
              <span>{meta.total} total · page {meta.current_page} of {meta.total_pages || 1}</span>
              <div className="flex gap-2">
                <button disabled={!meta.has_prev_page} onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 disabled:opacity-40">Prev</button>
                <button disabled={!meta.has_next_page} onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 disabled:opacity-40">Next</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
export default Registrations;
